import React from "react";
import { Box, Typography } from "@mui/material";
import { FormatFullName } from "app/utils/helper";
import moment from "moment";

const ElegantCertificate = ({
  student,
  schoolDetail,
  selectedSession,
  checkBoxEnabled = {},
  certificateTitle = "Certificate of Achievement",
  certificateSubtitle = "This is to certify that",
  courseName = "Academic Excellence",
  description = "Has successfully completed the academic requirements",
  primaryColor = "#1e3a8a",
  secondaryColor = "#d4af37",
  accentColor = "#991b1b",
  fontFamily = "Georgia",
  pageOrientation = "landscape",
  fieldStyles = {},
}) => {
  const studentFullName = FormatFullName(
    student?.firstName,
    student?.middleName,
    student?.lastName
  );

  const currentDate = moment().format("DD MMMM, YYYY");
  const certificateNumber = `CERT-${selectedSession}-${
    student?.admissionNumber ||
    student?.srNumber ||
    Math.floor(Math.random() * 10000)
  }`;

  const isLandscape = pageOrientation === "landscape";

  return (
    <Box
      sx={{
        width: isLandscape ? "297mm" : "210mm",
        height: isLandscape ? "210mm" : "297mm",
        margin: "0 auto",
        padding: "0",
        fontFamily: fontFamily || "Georgia",
        backgroundColor: "#ffffff",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <Box
        sx={{
          border: `3px solid ${primaryColor}`,
          padding: "10px",
          backgroundColor: "#ffffff",
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <Box
          sx={{
            border: `2px solid ${secondaryColor}`,
            padding: isLandscape ? "20px 40px" : "25px 20px",
            position: "relative",
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Decorative Top Flourish */}
            {/* <Box
              sx={{
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 200 20"
                sx={{
                  width: "250px",
                  height: "25px",
                }}
              >
                <path
                  d="M 0,10 Q 50,0 100,10 Q 150,20 200,10"
                  fill="none"
                  stroke={secondaryColor}
                  strokeWidth="2"
                />
              </Box>
            </Box> */}

            {/* Header */}
            <Box
              sx={{
                textAlign: "center",
                marginBottom: isLandscape ? "10px" : "15px",
              }}
            >
              {checkBoxEnabled.schoolLogo && (
                <Box
                  sx={{
                    marginBottom: "2px",
                  }}
                >
                  <Box
                    sx={{
                      width: isLandscape ? "100px" : "80px",
                      height: isLandscape ? "100px" : "80px",
                      margin: "0 auto",
                      borderRadius: "50%",
                      border: `3px solid ${secondaryColor}`,
                      padding: "10px",
                      backgroundColor: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={schoolDetail?.schoolLogo}
                      alt="School Logo"
                      style={{
                        height: isLandscape ? "80px" : "60px",
                        width: isLandscape ? "80px" : "60px",
                        objectFit: "contain",
                        border: `1px solid ${secondaryColor}`,
                        borderRadius: "50%",
                      }}
                    />
                  </Box>
                </Box>
              )}

              {checkBoxEnabled.schoolName && (
                <Typography
                  sx={{
                    fontSize: isLandscape ? "36px" : "28px",
                    fontWeight: 700,
                    color: primaryColor,
                    fontFamily: fontFamily,
                    marginBottom: "4px",
                    letterSpacing: "0.5px",
                  }}
                >
                  {schoolDetail?.schoolName || "School Name"}
                </Typography>
              )}

              {checkBoxEnabled.schoolAddress && (
                <Typography
                  sx={{
                    fontSize: isLandscape ? "16px" : "14px",
                    color: "#6b7280",
                    fontFamily: fontFamily,
                    marginBottom: "4px",
                  }}
                >
                  {schoolDetail?.schoolAddress?.address || "School Address"}
                </Typography>
              )}

              <Box
                sx={{
                  width: isLandscape ? "180px" : "140px",
                  height: "3px",
                  backgroundColor: secondaryColor,
                  margin: isLandscape ? "18px auto 0" : "10px auto 0",
                }}
              />
            </Box>

            {/* Certificate Title */}
            {checkBoxEnabled.certificateTitle && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: isLandscape ? "20px" : "10px",
                  marginBottom: "8px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: isLandscape ? "32px" : "24px",
                    color: secondaryColor,
                  }}
                >
                  ✦
                </Typography>
                <Typography
                  sx={{
                    fontSize: `${
                      fieldStyles.certificateTitle?.fontSize ||
                      (isLandscape ? 52 : 36)
                    }px`,
                    fontWeight: fieldStyles.certificateTitle?.fontWeight || 700,
                    color:
                      fieldStyles.certificateTitle?.color || secondaryColor,
                    fontFamily:
                      fieldStyles.certificateTitle?.fontFamily || fontFamily,
                    fontStyle:
                      fieldStyles.certificateTitle?.fontStyle || "italic",
                    textDecoration:
                      fieldStyles.certificateTitle?.textDecoration || "none",
                    letterSpacing: "2px",
                    textAlign: "center",
                  }}
                >
                  {certificateTitle}
                </Typography>
                <Typography
                  sx={{
                    fontSize: isLandscape ? "32px" : "24px",
                    color: secondaryColor,
                  }}
                >
                  ✦
                </Typography>
              </Box>
            )}

            {/* Main Content */}
            <Box sx={{ textAlign: "center" }}>
              {checkBoxEnabled.certificateSubtitle && (
                <Typography
                  sx={{
                    fontSize: `${
                      fieldStyles.certificateSubtitle?.fontSize ||
                      (isLandscape ? 22 : 18)
                    }px`,
                    fontWeight:
                      fieldStyles.certificateSubtitle?.fontWeight || "normal",
                    color: fieldStyles.certificateSubtitle?.color || "#4b5563",
                    fontFamily:
                      fieldStyles.certificateSubtitle?.fontFamily || fontFamily,
                    fontStyle:
                      fieldStyles.certificateSubtitle?.fontStyle || "italic",
                    textDecoration:
                      fieldStyles.certificateSubtitle?.textDecoration || "none",
                    marginBottom: isLandscape ? "12px" : "8px",
                  }}
                >
                  {certificateSubtitle}
                </Typography>
              )}

              {checkBoxEnabled.studentName && (
                <Box
                  sx={{
                    marginBottom: isLandscape ? "2px" : "10px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: `${
                        fieldStyles.studentName?.fontSize ||
                        (isLandscape ? 46 : 32)
                      }px`,
                      fontWeight: fieldStyles.studentName?.fontWeight || 700,
                      color: fieldStyles.studentName?.color || primaryColor,
                      fontFamily:
                        fieldStyles.studentName?.fontFamily || fontFamily,
                      fontStyle: fieldStyles.studentName?.fontStyle || "italic",
                      textDecoration:
                        fieldStyles.studentName?.textDecoration || "none",
                      marginBottom: "6px",
                    }}
                  >
                    {studentFullName}
                  </Typography>
                  {/* <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      color: secondaryColor,
                    }}
                  >
                    <Box
                      sx={{
                        width: isLandscape ? "70px" : "50px",
                        height: "2px",
                        backgroundColor: "currentColor",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: isLandscape ? "18px" : "14px",
                      }}
                    >
                      ◆
                    </Typography>
                    <Box
                      sx={{
                        width: isLandscape ? "70px" : "50px",
                        height: "2px",
                        backgroundColor: "currentColor",
                      }}
                    />
                  </Box> */}
                </Box>
              )}

              {checkBoxEnabled.class && (
                <Typography
                  sx={{
                    fontSize: isLandscape ? "18px" : "16px",
                    color: "#6b7280",
                    fontFamily: fontFamily,
                    marginBottom: isLandscape ? "12px" : "8px",
                  }}
                >
                  of Class{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: primaryColor }}
                  >
                    {student?.className || "N/A"}
                  </Box>{" "}
                  - Section{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: primaryColor }}
                  >
                    {student?.section || "N/A"}
                  </Box>
                </Typography>
              )}

              {checkBoxEnabled.courseName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: isLandscape ? "18px" : "12px",
                    margin: isLandscape ? "14px auto" : "10px auto",
                  }}
                >
                  <Box
                    sx={{
                      width: isLandscape ? "100px" : "60px",
                      height: "3px",
                      backgroundColor: accentColor,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: `${
                        fieldStyles.courseName?.fontSize ||
                        (isLandscape ? 28 : 22)
                      }px`,
                      fontWeight: fieldStyles.courseName?.fontWeight || 700,
                      color: fieldStyles.courseName?.color || "#1f2937",
                      fontFamily:
                        fieldStyles.courseName?.fontFamily || fontFamily,
                      fontStyle: fieldStyles.courseName?.fontStyle || "normal",
                      textDecoration:
                        fieldStyles.courseName?.textDecoration || "none",
                      padding: isLandscape ? "10px 15px" : "5px 10px",
                      borderTop: "3px solid currentColor",
                      borderBottom: "3px solid currentColor",
                    }}
                  >
                    {courseName}
                  </Typography>
                  <Box
                    sx={{
                      width: isLandscape ? "100px" : "60px",
                      height: "3px",
                      backgroundColor: accentColor,
                    }}
                  />
                </Box>
              )}

              {checkBoxEnabled.description && (
                <Typography
                  sx={{
                    fontSize: `${
                      fieldStyles.description?.fontSize ||
                      (isLandscape ? 20 : 16)
                    }px`,
                    fontWeight: fieldStyles.description?.fontWeight || "normal",
                    color: fieldStyles.description?.color || "#4b5563",
                    fontFamily:
                      fieldStyles.description?.fontFamily || fontFamily,
                    fontStyle: fieldStyles.description?.fontStyle || "italic",
                    textDecoration:
                      fieldStyles.description?.textDecoration || "none",
                    lineHeight: 1.8,
                    maxWidth: "85%",
                    margin: isLandscape ? "12px auto" : "8px auto",
                  }}
                >
                  {description}
                </Typography>
              )}

              {checkBoxEnabled.academicSession && (
                <Box
                  sx={{
                    display: "inline-block",
                    border: `3px solid ${primaryColor}`,
                    borderRadius: "30px",
                    padding: isLandscape ? "12px 15px" : "8px 12px",
                    marginTop: isLandscape ? "12px" : "8px",
                    backgroundColor: `${secondaryColor}10`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isLandscape ? "18px" : "14px",
                      fontWeight: 600,
                      fontFamily: fontFamily,
                      color: "#1f2937",
                    }}
                  >
                    Academic Session: {selectedSession}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Signatures */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                marginTop: "auto",
                paddingTop: "45px",
                marginBottom: "auto",
              }}
            >
                 <Box sx={{ textAlign: "center", flex: 1 }}>
                {checkBoxEnabled.principalSignature && (
                  <>
                    <Box
                      sx={{
                        width: "180px",
                        height: "3px",
                        backgroundColor: secondaryColor,
                        margin: "0 auto 10px",
                      }}
                    />
                    {/* <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#1f2937",
                        fontFamily: fontFamily,
                        marginBottom: "4px",
                      }}
                    >
                      {principalName}
                    </Typography> */}
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#6b7280",
                        fontFamily: fontFamily,
                        fontStyle: "italic",
                      }}
                    >
                      Principal
                    </Typography>
                  </>
                )}
              </Box>
              
              <Box sx={{ textAlign: "center", flex: 1 }}>
                {checkBoxEnabled.coordinatorSignature && (
                  <>
                    <Box
                      sx={{
                        width: "180px",
                        height: "3px",
                        backgroundColor: secondaryColor,
                        margin: "0 auto 10px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#6b7280",
                        fontFamily: fontFamily,
                        fontStyle: "italic",
                      }}
                    >
                      Academic Coordinator
                    </Typography>
                  </>
                )}
              </Box>

              {/* {checkBoxEnabled.seal && (
                <Box
                  sx={{
                    width: "110px",
                    height: "110px",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      border: "4px double #1f2937",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "80%",
                        height: "80%",
                        borderRadius: "50%",
                        border: "3px solid #6b7280",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 700,
                          textAlign: "center",
                          lineHeight: 1.5,
                          color: "#1f2937",
                          fontFamily: fontFamily,
                        }}
                      >
                        OFFICIAL
                        <br />
                        SEAL
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )} */}

           
            </Box>

            {/* Footer Info */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "18px",
                borderTop: "2px solid #e5e7eb",
              }}
            >
              {/* {checkBoxEnabled.certificateNumber && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#9ca3af",
                    fontFamily: fontFamily,
                  }}
                >
                  Certificate No:{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: "#6b7280" }}
                  >
                    {certificateNumber}
                  </Box>
                </Typography>
              )} */}
              {checkBoxEnabled.date && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#9ca3af",
                    fontFamily: fontFamily,
                  }}
                >
                  Date of Issue:{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: "#6b7280" }}
                  >
                    {currentDate}
                  </Box>
                </Typography>
              )}
            </Box>

            {/* Decorative Bottom Flourish */}
            {/* <Box
              sx={{
                textAlign: "center",
                marginTop: "20px",
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 200 20"
                sx={{
                  width: "250px",
                  height: "25px",
                }}
              >
                <path
                  d="M 0,10 Q 50,20 100,10 Q 150,0 200,10"
                  fill="none"
                  stroke={secondaryColor}
                  strokeWidth="2"
                />
              </Box>
            </Box> */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ElegantCertificate;
